import { useState, useEffect, useRef, useContext } from "react";
import { useQueryClient, isCancelledError } from "@tanstack/react-query";
import {
  Text,
  Heading,
  SelectBoxGroup,
  SelectBox,
  Switch,
  ActionButton,
} from "@react-spectrum/s2";
import { useDescribeMogrtTemplate } from "@app/hooks/mogrtTemplates";
import { useAudioMux } from "@app/hooks/textToSpeech";
import { presignURLQueryOptions, usePresignURL } from "@app/hooks/s3";
import { videoKey } from "@app/hooks/videos";
import { useUserProfile } from "@app/hooks/useIMS";
import { trackVoiceoverEvent, AnalyticsEventNames } from "@app/utils/analytics";
import { VisuallyHidden } from "@app/components/VisuallyHidden";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { downloadFile } from "@app/services/downloadFile";
import {
  MUSIC_BED_VOLUME,
  DEFAULT_VOICEOVER_VOLUME,
} from "@app/constants/audio";
import { VoiceoverAsset, resolveVoiceoverTemplateText } from "./VoiceoverAsset";
import { VoiceoverTimeline } from "./VoiceoverTimeline";
import {
  VoiceoverUpload,
  UPLOAD_MUX_SOURCE_KEY,
  UPLOADED_VOICEOVER_ASSET_NAME,
} from "./VoiceoverUpload";
import { Step } from "./Step";
import AudioWaveIcon from "@react-spectrum/s2/illustrations/linear/AudioWave";
import MicrophoneOffIcon from "@react-spectrum/s2/illustrations/linear/MicrophoneOff";
import ReplaceIcon from "@react-spectrum/s2/illustrations/linear/Replace";
import UndoIcon from "@react-spectrum/s2/icons/Undo";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const VOICEOVER_MODE = {
  ORIGINAL: "original",
  EDIT: "edit",
  UPLOAD: "upload",
  NONE: "none",
};

export const VoiceoverStep = ({ onContinue, onBack, continueLabel }) => {
  const {
    videoId,
    video,
    template,
    updateVideo,
    uploadAssets,
    updateAssets,
    stepActionCtx,
  } = useContext(VideoWizardContext);
  const queryClient = useQueryClient();
  const userProfile = useUserProfile();
  const audioMux = useAudioMux();

  const voiceoverPreviewUrl = usePresignURL(template.data?.voiceoverPreviewUrl);

  const customizeRef = useRef(null);
  const didMountRef = useRef(false);

  const [uploadedVoiceoverFile, setUploadedVoiceoverFile] = useState(null);

  const [_hasCustomizedVoiceover, setHasCustomizedVoiceover] = useState();
  const hasCustomizedVoiceover =
    _hasCustomizedVoiceover ??
    (!!video.data?.voiceoverMode
      ? video.data?.voiceoverMode !== VOICEOVER_MODE.ORIGINAL
      : false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (hasCustomizedVoiceover) {
      customizeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [hasCustomizedVoiceover]);

  const [_selectedKeys, setSelectedKeys] = useState(new Set());
  const selectedKeys = _selectedKeys.size
    ? _selectedKeys
    : video.data?.voiceoverMode
      ? new Set([video.data?.voiceoverMode])
      : new Set();

  const templateAssets = template.data?.assets?.filter(
    (asset) => asset.type === "audio",
  );

  const audioAssets =
    templateAssets
      ?.map((templateAsset) =>
        video.data?.assets?.find((asset) => asset.name === templateAsset.name),
      )
      ?.filter(Boolean) ?? [];

  const selectedCampaign = video.data?.campaigns?.find(
    (item) => item.id === video.data?.selectedCampaignIds?.[0],
  );

  const areAllAudioAssetsSelected =
    ((templateAssets?.length ?? 0) > 0 &&
      templateAssets.every((asset) =>
        video.data?.assets?.some((a) => a.name === asset.name && !a.isInvalid),
      )) ??
    false;

  const audioVariations = template.data?.audioVariations;

  const describeMogrtTemplate = useDescribeMogrtTemplate(
    video.data?.mogrtRootPath,
    {
      enabled:
        hasCustomizedVoiceover &&
        !!video.data?.mogrtRootPath &&
        video.data?.templateType === "mogrt",
    },
  );

  const musicPath =
    video.data?.musicPath ?? describeMogrtTemplate.data?.musicPath ?? "";

  const noVoMusicPath = describeMogrtTemplate.data?.musicNoVoPath ?? musicPath;

  const existingMuxAsset = video.data?.assets?.find(
    (a) => a.name === audioVariations?.[0]?.key,
  );

  const isUpload = selectedKeys.has(VOICEOVER_MODE.UPLOAD);
  const uploadedRawAsset = video.data?.assets?.find(
    (a) => a.name === UPLOADED_VOICEOVER_ASSET_NAME,
  );
  const uploadMuxKey =
    isUpload && uploadedRawAsset?.filePath
      ? `${UPLOAD_MUX_SOURCE_KEY}:${uploadedRawAsset.filePath}:${uploadedRawAsset.generatedAt ?? 0}:${musicPath}`
      : null;
  const isUploadMuxReady =
    !!uploadMuxKey && existingMuxAsset?.muxSourceKey === uploadMuxKey;

  const isEdit = selectedKeys.has(VOICEOVER_MODE.EDIT);

  const uploadMuxKickedRef = useRef(null);

  const runMusicOnlyMux = async () => {
    if (!audioVariations?.length || !noVoMusicPath) {
      return;
    }

    const muxKey = `none:${noVoMusicPath}`;
    const videoData = queryClient.getQueryData(videoKey.detail(videoId));
    const existing = videoData?.assets?.find(
      (a) => a.name === audioVariations[0]?.key,
    );
    if (existing?.muxSourceKey === muxKey) {
      return;
    }

    try {
      const outputDuration =
        audioVariations.reduce(
          (max, v) =>
            Math.max(
              max,
              (v.settings?.inset ?? 0) + (v.settings?.duration ?? 0),
            ),
          0,
        ) + 5;

      const first = audioVariations[0];
      const firstSettings = first?.settings ?? {};

      await updateAssets.mutateAsync({
        assets: [
          {
            name: first.key,
            type: first.type,
            filePath: noVoMusicPath,
            referenceKey: first.referenceKey,
            volume: firstSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
            inset: 0,
            start: 0,
            duration: outputDuration,
            muxSourceKey: muxKey,
          },
        ],
      });
    } catch (e) {
      if (isCancelledError(e)) {
        return;
      }
      console.error("Music-only audio assignment failed:", e);
    }
  };

  const runUploadMux = async () => {
    if (!audioVariations?.length || audioMux.isPending) {
      return;
    }

    const videoData = queryClient.getQueryData(videoKey.detail(videoId));
    const rawAsset = videoData?.assets?.find(
      (a) => a.name === UPLOADED_VOICEOVER_ASSET_NAME,
    );
    if (!rawAsset?.filePath) {
      return;
    }

    const muxKey = `${UPLOAD_MUX_SOURCE_KEY}:${rawAsset.filePath}:${rawAsset.generatedAt ?? 0}:${musicPath}`;
    const existing = videoData?.assets?.find(
      (a) => a.name === audioVariations[0]?.key,
    );
    if (existing?.muxSourceKey === muxKey) {
      return;
    }

    try {
      const outputDuration =
        audioVariations.reduce(
          (max, v) =>
            Math.max(
              max,
              (v.settings?.inset ?? 0) + (v.settings?.duration ?? 0),
            ),
          0,
        ) + 5;

      const uploadPresign = await queryClient.query(
        presignURLQueryOptions(rawAsset.filePath),
      );

      if (!uploadPresign?.presignURL) {
        return;
      }

      const first = audioVariations[0];
      const firstSettings = first?.settings ?? {};

      const audios = [];

      if (musicPath) {
        const musicPresign = await queryClient.query(
          presignURLQueryOptions(musicPath),
        );

        if (!musicPresign?.presignURL) {
          return;
        }

        audios.push({
          source: { url: musicPresign.presignURL },
          volume: MUSIC_BED_VOLUME,
          inset: 0,
          start: 0,
          duration: outputDuration,
        });
      }

      audios.push({
        source: { url: uploadPresign.presignURL },
        volume: firstSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
        inset: 0,
        start: 0,
        duration: outputDuration,
      });

      const muxUrl = await audioMux.mutateAsync(audios);

      await uploadAssets.mutateAsync({
        assets: [
          {
            name: first.key,
            type: first.type,
            src: muxUrl,
            referenceKey: first.referenceKey,
            volume: firstSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
            inset: 0,
            start: 0,
            duration: outputDuration,
            muxSourceKey: muxKey,
            generatedAt: Date.now(),
          },
        ],
      });
    } catch (e) {
      if (isCancelledError(e)) {
        return;
      }
      console.error("Uploaded voiceover audio mux failed:", e);
    }
  };

  const isGenerating = template.data?.assets?.some(
    (asset) =>
      asset.type === "audio" &&
      !video.data?.assets?.find((a) => a.name === asset.name),
  );

  const uploadAudioKey = template.data?.audioVariations?.[0]?.key;
  const hasUploadedVoiceover = !!uploadedRawAsset;

  useEffect(() => {
    if (!isUpload || !hasUploadedVoiceover || !uploadMuxKey) {
      return;
    }
    if (isUploadMuxReady) {
      uploadMuxKickedRef.current = null;
      return;
    }
    if (uploadMuxKickedRef.current === uploadMuxKey || audioMux.isPending) {
      return;
    }
    uploadMuxKickedRef.current = uploadMuxKey;
    runUploadMux();
  }, [
    isUpload,
    hasUploadedVoiceover,
    uploadMuxKey,
    isUploadMuxReady,
    audioMux.isPending,
  ]);

  const isNextDisabled =
    templateAssets?.length === 1
      ? !areAllAudioAssetsSelected
      : hasCustomizedVoiceover
        ? selectedKeys.size < 1 ||
          (selectedKeys.has(VOICEOVER_MODE.EDIT) &&
            !areAllAudioAssetsSelected) ||
          (selectedKeys.has(VOICEOVER_MODE.UPLOAD) && !hasUploadedVoiceover)
        : false;

  const handleSelectionChange = (keys) => {
    setSelectedKeys(keys);

    const nextMode = Array.from(keys)[0];

    if (!keys.has(video.data?.voiceoverMode)) {
      updateVideo.mutate(
        { voiceoverMode: nextMode },
        {
          onSuccess: () => {
            if (nextMode === VOICEOVER_MODE.NONE) {
              runMusicOnlyMux();
            }
          },
        },
      );
    } else if (nextMode === VOICEOVER_MODE.NONE) {
      runMusicOnlyMux();
    }
  };

  const handleCustomizeToggle = (isSelected) => {
    setHasCustomizedVoiceover(isSelected);
    handleSelectionChange(
      new Set([isSelected ? VOICEOVER_MODE.EDIT : VOICEOVER_MODE.ORIGINAL]),
    );
  };

  const handleDeleteUploadedVoiceover = () => {
    setUploadedVoiceoverFile(null);
    const toRemove = [{ name: UPLOADED_VOICEOVER_ASSET_NAME }];
    if (uploadAudioKey) {
      toRemove.push({ name: uploadAudioKey });
    }
    updateAssets.mutate({ toRemove });
  };

  const handleDownloadScript = () => {
    const sections = (templateAssets ?? [])
      .map((templateAsset) => {
        const asset = video.data?.assets?.find(
          (a) => a.name === templateAsset.name,
        );
        const resolvedText = resolveVoiceoverTemplateText(
          asset?.text ?? templateAsset.text ?? "",
          {
            parts: asset?.parts,
            campaign: selectedCampaign,
          },
        ).trim();

        if (!resolvedText) {
          return null;
        }

        return `${resolvedText}\n`;
      })
      .filter(Boolean);

    if (!sections.length) {
      return;
    }

    const content = `# ${video.data?.name ?? video.data?.id ?? "Sizzle reel"} script\n\n${sections.join("\n")}\n`;
    const projectName = video.data?.name ?? video.data?.id ?? "sizzle-reel";
    downloadFile(content, `${projectName}-script.md`, "text/markdown");
  };

  const handleOnContinue = (...args) => {
    if (hasCustomizedVoiceover) {
      trackVoiceoverEvent(AnalyticsEventNames.VOICEOVER_CHOSEN, {
        user: { email: userProfile?.email },
        videoId: video.data?.id,
        templateId: video.data?.templateId,
        voiceoverTemplate:
          Array.from(selectedKeys)[0] ?? video.data?.voiceoverMode,
      });
    }

    if (isEdit) {
      stepActionCtx?.runVoiceoverMux?.();
    }

    onContinue(...args);
  };

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          gap: 32,
          size: "full",
        })}
      >
        {templateAssets?.length > 1 && (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 32,
              size: "full",
              // maxWidth: 1000,
              marginX: "auto",
              marginBottom: 64,
            })}
          >
            <Step.Header
              title={
                template?.data.id === "ao-overview"
                  ? "Your voiceover is ready"
                  : "Voiceover"
              }
              subtitle={
                template?.data.id === "ao-overview"
                  ? "We recommend checking the pronunciation of your brand and campaign names before continuing."
                  : "Your video will use the default voiceover from the template"
              }
            />

            <Switch
              isSelected={hasCustomizedVoiceover}
              onChange={handleCustomizeToggle}
              styles={style({ marginX: "auto" })}
            >
              {template?.data.id === "ao-overview"
                ? "Review voiceover"
                : "Want to customize?"}
            </Switch>

            {!!hasCustomizedVoiceover && (
              <>
                <div
                  ref={customizeRef}
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginBottom: 12,
                  })}
                >
                  <Heading styles={style({ font: "title", marginY: 0 })}>
                    These features are experimental
                  </Heading>
                  <Text styles={style({ font: "detail", textAlign: "center" })}>
                    Feel free to try them out, but keep in mind things might not
                    work perfectly just yet.
                  </Text>
                </div>

                <SelectBoxGroup
                  aria-label="Select voiceover assets to include in the video"
                  selectionMode="single"
                  onSelectionChange={handleSelectionChange}
                  selectedKeys={selectedKeys}
                  styles={style({ marginBottom: 64 })}
                >
                  <SelectBox
                    id={VOICEOVER_MODE.EDIT}
                    textValue="Edit voiceover"
                  >
                    <AudioWaveIcon />
                    <Text slot="label">Edit voiceover</Text>
                  </SelectBox>
                  <SelectBox
                    id={VOICEOVER_MODE.UPLOAD}
                    textValue="Upload voiceover"
                  >
                    <ReplaceIcon />
                    <Text slot="label">Upload voiceover</Text>
                  </SelectBox>
                  <SelectBox id={VOICEOVER_MODE.NONE} textValue="No voiceover">
                    <MicrophoneOffIcon />
                    <Text slot="label">No voiceover</Text>
                  </SelectBox>
                </SelectBoxGroup>

                <Step.Header>
                  {selectedKeys.has(VOICEOVER_MODE.EDIT) && (
                    <Text
                      styles={style({ font: "body-xl", textAlign: "center" })}
                    >
                      Check the voiceover audio, which will be used in the
                      video.
                      <br />
                      Fix pronunciation and generate again if needed.
                    </Text>
                  )}
                  {selectedKeys.has(VOICEOVER_MODE.UPLOAD) && (
                    <div
                      className={style({
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 24,
                      })}
                    >
                      <Text
                        styles={style({ font: "body-xl", textAlign: "center" })}
                      >
                        Upload your own voiceover audio file.
                        <br />
                        Match the script, timing, and volume of the sample
                        below.
                        <br />
                        Background music will be added automatically.
                      </Text>
                      <div
                        className={style({
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 8,
                        })}
                      >
                        {voiceoverPreviewUrl.data?.presignURL && (
                          <div
                            className={style({
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 2,
                            })}
                          >
                            <ActionButton
                              isQuiet
                              onPress={() =>
                                window.open(
                                  voiceoverPreviewUrl.data?.presignURL,
                                  "_blank",
                                )
                              }
                            >
                              <DownloadIcon />
                              <Text>Download sample voiceover</Text>
                            </ActionButton>
                            <Text
                              styles={style({
                                font: "detail",
                                color: "neutral-subdued",
                                textAlign: "center",
                              })}
                            >
                              Aim for a loudness similar to the sample (-23
                              LUFS, peak around -5 dB)
                            </Text>
                          </div>
                        )}
                        {templateAssets?.length > 0 && (
                          <ActionButton isQuiet onPress={handleDownloadScript}>
                            <DownloadIcon />
                            <Text>Download sizzle reel script</Text>
                          </ActionButton>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedKeys.has(VOICEOVER_MODE.NONE) && (
                    <Text
                      styles={style({ font: "body-xl", textAlign: "center" })}
                    >
                      Exclude the voiceover from the video.
                      <br />
                      Only background music and sound effects will be included
                      in the final video.
                    </Text>
                  )}
                </Step.Header>

                {selectedKeys.has(VOICEOVER_MODE.EDIT) && isGenerating && (
                  <Step.Loading
                    title="Generating voiceover"
                    subtitle="This may take a few moments, so please keep this tab open."
                    ariaLabel="Generating voiceover"
                    icon={
                      <AudioWaveIcon
                        styles={style({ size: 160, marginTop: 64 })}
                      />
                    }
                  />
                )}

                {selectedKeys.has(VOICEOVER_MODE.EDIT) && (
                  <VisuallyHidden isHidden={isGenerating}>
                    {templateAssets?.length > 1 && (
                      <VoiceoverTimeline
                        videoId={video.data?.id}
                        previewUrl={template.data?.timelinePreviewUrl}
                      />
                    )}

                    {templateAssets?.length === 1 && (
                      <div
                        className={style({ maxWidth: 1000, marginX: "auto" })}
                      >
                        {audioAssets?.map((asset) => (
                          <VoiceoverAsset
                            key={asset.name}
                            videoId={video.data?.id}
                            assetName={asset.name}
                            selectedCampaign={selectedCampaign}
                            segments={asset?.segments ?? []}
                            editableScript={template}
                          />
                        ))}
                      </div>
                    )}
                  </VisuallyHidden>
                )}

                {selectedKeys.has(VOICEOVER_MODE.UPLOAD) && (
                  <VoiceoverUpload
                    videoId={video.data?.id}
                    file={uploadedVoiceoverFile}
                    onFileChange={setUploadedVoiceoverFile}
                  />
                )}

                <div
                  className={style({
                    display: "flex",
                    gap: 8,
                    marginX: "auto",
                  })}
                >
                  <ActionButton
                    size="S"
                    onPress={() => {
                      trackVoiceoverEvent(AnalyticsEventNames.VOICEOVER_RESET, {
                        user: { email: userProfile?.email },
                        videoId: video.data?.id,
                        templateId: video.data?.templateId,
                      });
                      handleCustomizeToggle(false);
                    }}
                    isQuiet
                  >
                    <UndoIcon />
                    <Text>Reset to default</Text>
                  </ActionButton>
                  {selectedKeys.has(VOICEOVER_MODE.UPLOAD) &&
                    (!!uploadedVoiceoverFile || hasUploadedVoiceover) && (
                      <ActionButton
                        size="S"
                        onPress={handleDeleteUploadedVoiceover}
                        isQuiet
                      >
                        <DeleteIcon />
                        <Text>Delete uploaded file</Text>
                      </ActionButton>
                    )}
                </div>
              </>
            )}
          </div>
        )}

        {/* FIXME: remove after migration to new VO edit flow */}
        {templateAssets?.length === 1 && (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 32,
              size: "full",
              // maxWidth: 1000,
              marginX: "auto",
              marginBottom: 64,
            })}
          >
            <div className={style({ maxWidth: 1000, marginX: "auto" })}>
              {templateAssets?.map((asset) => (
                <VoiceoverAsset
                  key={asset.name}
                  videoId={video.data?.id}
                  assetName={asset.name}
                  isStandalone
                />
              ))}
            </div>
          </div>
        )}
      </Step>

      <Step.Actions
        onBack={onBack}
        onContinue={(...args) => handleOnContinue(...args)}
        continueLabel={continueLabel}
        continueVariant="primary"
        isContinueDisabled={isNextDisabled}
      />
    </>
  );
};
